// Shared square action-tile row.
//
// A footer band of 48-px square buttons (icon + 8-px uppercase caption) sharing
// the `.domain-strip-tab` visual language. Used by panes whose primary verb is
// an accumulating action — Contour's Add/Clear, Field Lines' Trace/Clear. The
// row is raw DOM (cached on the folder by each caller) so it survives pane
// child disposal between rebuilds; the click listeners persist with it.

export interface ActionTileSpec {
    /** Optional stable handle (`data-action-id`) so a caller can re-find a tile
     *  and relabel it — used by toggle tiles whose caption tracks state. */
    id?: string;
    /** Extra class on the tile, e.g. `'is-clear'` for the destructive red hover. */
    cls?: string;
    /** Font Awesome icon class, e.g. `'fa-plus'`. */
    icon: string;
    /** Visible caption + aria-label + title. */
    label: string;
    onClick: () => void;
}

/** Build a `.pane-actions` row from tile specs. Created once per folder
 *  lifetime; the listeners persist across rebuilds via the caller's cache. */
export function buildActionTiles(specs: readonly ActionTileSpec[]): HTMLDivElement {
    const row = document.createElement('div');
    row.className = 'pane-actions';
    for (const spec of specs) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = `pane-action${spec.cls ? ` ${spec.cls}` : ''}`;
        if (spec.id) btn.dataset['actionId'] = spec.id;
        btn.setAttribute('aria-label', spec.label);
        btn.title = spec.label;
        btn.innerHTML = `<i class="fas ${spec.icon}" aria-hidden="true"></i><span class="pane-action-label">${spec.label}</span>`;
        // Drive hover via a JS-toggled `.is-hover` class, not the CSS `:hover`
        // pseudo. Desktop WebKit leaves the highlight stuck on these tiles after
        // a click: it loses the post-click pointer-leave for the element, so
        // neither `:hover` nor a leave-only class toggle ever clears (the domain
        // tiles dodge this because their aria-selected flip forces a restyle).
        // So we also drop the class on click — the highlight clears immediately
        // and re-lights on the next pointerenter. Chrome behaves identically.
        btn.addEventListener('pointerenter', () => { btn.classList.add('is-hover'); });
        btn.addEventListener('pointerleave', () => { btn.classList.remove('is-hover'); });
        btn.addEventListener('pointercancel', () => { btn.classList.remove('is-hover'); });
        btn.addEventListener('click', () => { btn.classList.remove('is-hover'); btn.blur(); spec.onClick(); });
        row.append(btn);
    }
    return row;
}

/** Relabel a visibility toggle tile (eye icon + Hide/Show caption) to reflect
 *  state. The caption is the *action* — Hide when visible, Show when hidden — so
 *  clicking always does what it says. `noun` names the target for the aria-label
 *  (e.g. 'field lines', 'contours'). */
export function syncVisibilityTile(row: HTMLDivElement, id: string, visible: boolean, noun: string): void {
    const btn = row.querySelector<HTMLButtonElement>(`[data-action-id="${id}"]`);
    if (!btn) return;
    const label = visible ? 'Hide' : 'Show';
    const icon = visible ? 'fa-eye-slash' : 'fa-eye';
    btn.title = label;
    btn.setAttribute('aria-label', `${label} ${noun}`);
    btn.setAttribute('aria-pressed', String(visible));
    btn.innerHTML = `<i class="fas ${icon}" aria-hidden="true"></i><span class="pane-action-label">${label}</span>`;
}
